import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight } from "@material-symbols-svg/react";
import { ChevronLeft, ChevronRight as ChevronRightIcon } from "lucide-react";
import moment from "moment";

import Tabs from "src/components/app/Tabs";
import Dropdown, { DropdownOption } from "src/components/common/DropdownV2";
import AddButtonOverlay from "src/components/common/AddButtonOverlay";
import MedicationScheduleChart from "src/components/app/charts/MedicationScheduleChart";
import InsightHistory from "./InsightHistory";
import InsightsProgressCard from "./InsightsProgressCard";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as medicationActions from "src/redux/slices/medicationSlice";
import type { InsightComponentHealthOption } from "./InsightComponentHealth";

type PeriodSegment = "daily" | "weekly" | "monthly";

interface MedicationInsightViewProps {
  supportProgramKey: string;
  options?: InsightComponentHealthOption[];
  icon?: React.ReactNode;
}

const filterTypeMap: Record<PeriodSegment, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

const MedicationInsightView: React.FC<MedicationInsightViewProps> = ({
  supportProgramKey,
  options = [],
  icon,
}) => {
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const { medicationsList, medicationLog } = useAppSelector(
    (state) => state.medication
  );

  const [viewType, setViewType] = useState<"chart" | "history">("chart");
  const [activeTab, setActiveTab] = useState<PeriodSegment>("weekly");
  const [periodOffset, setPeriodOffset] = useState(0);
  const [selectedMedicationId, setSelectedMedicationId] = useState<
    string | number
  >("");

  const medications = medicationsList?.data ?? [];
  const loadingList = medicationsList?.isLoading ?? false;
  const loadingLog = medicationLog?.isLoading ?? false;

  useEffect(() => {
    if (!patient?.data?.patient_id) return;
    new Promise((resolve, reject) => {
      dispatch(medicationActions.getMedicationListRequest({ resolve, reject }));
    }).catch(() => {});
  }, [dispatch, patient?.data?.patient_id]);

  useEffect(() => {
    if (medications.length > 0 && !selectedMedicationId) {
      setSelectedMedicationId(medications[0]._id);
    }
  }, [medications, selectedMedicationId]);

  const fetchLog = useCallback(() => {
    if (
      !patient?.data?.patient_id ||
      !selectedMedicationId ||
      medications.length === 0
    )
      return;
    const filterType = filterTypeMap[activeTab];
    const userId = patient?.data?.patient_id;
    if (!userId) return;

    const queryParams: Record<string, string | number> = {
      user_id: userId,
      filter_type: filterType,
      logger: "medication",
      medication_id: selectedMedicationId,
    };

    if (filterType === "day") {
      const date = moment().subtract(periodOffset, "day");
      queryParams.date = date.format("DD/MM/YYYY");
    } else if (filterType === "week") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      const end = start.clone().endOf("week");
      queryParams.start_date = start.format("DD/MM/YYYY");
      queryParams.end_date = end.format("DD/MM/YYYY");
    } else {
      const monthM = moment().subtract(periodOffset, "month").startOf("month");
      queryParams.month = monthM.format("MMM").toUpperCase();
      queryParams.year = monthM.year();
    }

    new Promise((resolve, reject) => {
      dispatch(
        medicationActions.getMedicationLogRequest({
          queryParams,
          resolve,
          reject,
        })
      );
    }).catch(() => {});
  }, [
    dispatch,
    patient?.data?.patient_id,
    selectedMedicationId,
    activeTab,
    periodOffset,
    medications.length,
  ]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const schedule = medicationLog?.data?.medication_schedule ?? [];

  const periodLabel = useMemo(() => {
    if (activeTab === "daily") {
      const d = moment().subtract(periodOffset, "day");
      return periodOffset === 0
        ? "Today"
        : periodOffset === 1
        ? "Yesterday"
        : d.format("D MMM");
    }
    if (activeTab === "weekly") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      const end = start.clone().endOf("week");
      return periodOffset === 0
        ? "This week"
        : `${start.format("D MMM")} – ${end.format("D MMM")}`;
    }
    const start = moment().subtract(periodOffset, "month").startOf("month");
    return periodOffset === 0 ? "This month" : start.format("MMM");
  }, [activeTab, periodOffset]);

  const chartPeriodStart = useMemo(() => {
    if (activeTab === "daily") {
      return moment().subtract(periodOffset, "day").startOf("week");
    }
    if (activeTab === "weekly") {
      return moment().subtract(periodOffset, "week").startOf("week");
    }
    return moment().subtract(periodOffset, "month").startOf("month");
  }, [activeTab, periodOffset]);

  const chartViewMode = activeTab === "monthly" ? "month" : "week";

  const dropdownOptions = useMemo(
    () =>
      medications.map((med: { _id: string; name: string }) => ({
        value: med._id,
        label: med.name,
      })),
    [medications]
  );

  const loading = loadingList || loadingLog;
  const hasMedications = medications.length > 0;

  if (viewType === "history") {
    return (
      <InsightHistory
        onBackClick={() => setViewType("chart")}
        supportProgramKey={supportProgramKey}
        logger="medication"
        icon={icon}
      />
    );
  }

  if (!hasMedications && !loadingList) {
    return (
      <div className="relative pb-12">
        <div className="px-4 space-y-4">
          <p className="text-sm text-gray-500">
            No medications added yet. Add a medication to see your schedule and
            progress here.
          </p>
          <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white p-8 flex flex-col items-center justify-center min-h-[220px]">
            <p className="text-sm font-medium text-gray-500 text-center">
              Add a medication to get started.
            </p>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <button
            type="button"
            onClick={() => setViewType("history")}
            className="w-full bg-white rounded-xl px-4 py-3 flex items-center justify-between shadow-sm border border-gray-100"
          >
            <span>Show All Data</span>
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
        <AddButtonOverlay options={options} />
      </div>
    );
  }

  return (
    <div className="relative pb-12">
      <div className="px-4 space-y-4">
        <InsightsProgressCard loggerType="medication" unit="doses" />

        <Dropdown
          className="bg-white"
          options={dropdownOptions as DropdownOption[]}
          value={selectedMedicationId}
          onChange={(val) => setSelectedMedicationId(val as string)}
        />

        {/* Medication selector */}
        <div className="bg-white rounded-xl p-4 space-y-4">
          {/* Period navigation + D/W/M */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setPeriodOffset((p) => p + 1)}
                className="bg-white rounded-full p-1.5 border border-gray-200"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-600 font-medium min-w-[4rem] text-center">
                {periodLabel}
              </span>
              <button
                type="button"
                onClick={() => setPeriodOffset((p) => Math.max(0, p - 1))}
                className="bg-white rounded-full p-1.5 border border-gray-200"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="w-1/6">
              <Tabs
                tabs={[
                  // { id: "daily", title: "D" },
                  { id: "weekly", title: "W" },
                  // { id: "monthly", title: "M" },
                ]}
                activeTab={activeTab}
                onChange={(tab) => {
                  setActiveTab(tab.id as PeriodSegment);
                  setPeriodOffset(0);
                }}
              />
            </div>
          </div>

          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[var(--app-primary)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <MedicationScheduleChart
              data={schedule}
              viewMode={chartViewMode}
              periodStart={chartPeriodStart}
            />
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <button
          type="button"
          onClick={() => setViewType("history")}
          className="w-full bg-white rounded-xl px-4 py-3 flex items-center justify-between shadow-sm border border-gray-100"
        >
          <span>Show All Data</span>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <AddButtonOverlay options={options} />
    </div>
  );
};

export default MedicationInsightView;
